import { useDeleteCommodityMutation } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import {
  ErrorModal,
  HelmetDexteel,
  MesfModal,
  useContextMenuMESF,
} from "@dexteel/mesf-core";
import { Button, Grid, IconButton, Typography } from "@mui/material";
import { PlaylistAdd as PlaylistAddIcon } from "@mui/icons-material";
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  IStatusPanelParams,
  MenuItemDef,
  RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CenteredLazyLoading } from "../../../../../controls/LazyLoading";
import { useGridDefinitions } from "../../../../../controls/ag-grid/components/GridDefinitions";
import { TextFieldSearchFilter } from "../../../../../shared/TextFieldSearchFilter";
import { Commodity } from "../../../models/Commodity";
import { useCommodityOptionsFunctions } from "../hooks/useCommodityOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CommodityDetails } from "./CommodityDetails";

const AddCommodityStatusPanel = ({
  handleNewCommodity,
}: {
  handleNewCommodity: () => void;
}) => {
  return (
    <IconButton
      onClick={handleNewCommodity}
      color="primary"
      style={{
        padding: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
      }}
    >
      <PlaylistAddIcon style={{ width: 30, height: 30 }} />
    </IconButton>
  );
};

type Props = {
  isLoading: boolean;
  commodities: Commodity[];
  commodityId: string | undefined;
};

export const TableCommodity = ({
  isLoading,
  commodities,
  commodityId,
}: Props) => {
  const navigate = useNavigate();

  // === TanStack Query Mutations ===
  const deleteCommodityMutation = useDeleteCommodityMutation();

  const [error, setError] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [showModal, setShowModal] = useState<"new" | "edit" | "delete" | "">(
    "",
  );
  const [commodity, setCommodity] = useState<Commodity | null>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [pendingSelectCommodityId, setPendingSelectCommodityId] = useState<
    number | null
  >(null);

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  // Handle pending commodity selection when data arrives
  useEffect(() => {
    if (pendingSelectCommodityId && gridApi) {
      const rowNode = gridApi.getRowNode(String(pendingSelectCommodityId));
      if (rowNode) {
        gridApi.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
        rowNode.setSelected(true);
        setCommodity(rowNode.data);
        setPendingSelectCommodityId(null);
      }
    }
  }, [commodities, pendingSelectCommodityId, gridApi]);

  const handleNewCommodity = () => {
    if (gridApi) gridApi.deselectAll();
    setShowModal("new");
    setCommodity(null);
    navigate(`/configuration/scrap-yard/commodities/new`);
  };

  const { getMenuOptions } = useCommodityOptionsFunctions({
    setCommodity,
    setShowModal,
    handleNewCommodity,
  });

  const { rows, columnDefs } = useTableData({
    commodities,
    setCommodity,
    setShowModal,
    showContextMenu,
  });

  const { columnSimpleDefaults } = useGridDefinitions({
    OnEdit: (data: Commodity) => {
      setShowModal("edit");
      setCommodity(data);
    },
  });

  const onRowSelected = (event: RowSelectedEvent) => {
    const cell = gridApi?.getFocusedCell();
    if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
      setCommodity(event.data);
      setShowModal("edit");
      navigate(
        `/configuration/scrap-yard/commodities/${
          (event.data as Commodity).CommodityId
        }`,
      );
    }
  };

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const data = params.node?.data;
      const nativeEvent = (
        params as GetContextMenuItemsParams & { event?: MouseEvent }
      ).event;

      if (data && nativeEvent) {
        showContextMenu(
          nativeEvent as unknown as React.MouseEvent,
          data,
          "commodityContext",
        );
        params.api.deselectAll();
        params.node?.setSelected(true);
      }
      return [];
    },
    [rows],
  );

  const onModalHide = ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => {
    if (close) {
      setShowModal("");
      setCommodity(null);
      if (gridApi) gridApi.deselectAll();
    }
    if (shouldUpdate && entityId) {
      // Try to select immediately, or store for pending selection
      const rowNode = gridApi?.getRowNode(String(entityId));
      if (rowNode && gridApi) {
        gridApi.ensureIndexVisible(rowNode.rowIndex ?? 0, "middle");
        rowNode.setSelected(true);
        setCommodity(rowNode.data);
      } else {
        setPendingSelectCommodityId(entityId);
      }
    }
    navigate(`/configuration/scrap-yard/commodities`);
  };

  useEffect(() => {
    if (commodityId) {
      if (commodityId === "new") {
        handleNewCommodity();
      } else {
        let commodityNode: IRowNode<Commodity> | undefined;

        gridApi?.forEachNode((n) => {
          if ((n.data as Commodity).CommodityId === Number(commodityId)) {
            commodityNode = n;
            return true;
          }
        });

        if (commodityNode) {
          gridApi?.ensureIndexVisible(commodityNode.rowIndex ?? 0, "middle");
          commodityNode.setSelected(true);
          setCommodity(commodityNode.data || null);
          setShowModal("edit");
        }
      }
    }
  }, [gridApi]);

  useEffect(() => {
    registerConfig({
      id: "commodityContext",
      getOptions: getMenuOptions,
    });
  }, []);

  const handleDeleteCommodity = () => {
    if (!commodity?.CommodityId) return;

    deleteCommodityMutation.mutate(commodity.CommodityId, {
      onSuccess: () => {
        setShowModal("");
        setCommodity(null);
        if (gridApi) gridApi.deselectAll();
        navigate(`/configuration/scrap-yard/commodities`);
      },
      onError: (err) => {
        setError(
          err instanceof Error ? err.message : "Failed to delete commodity",
        );
      },
    });
  };

  return (
    <>
      <HelmetDexteel title={"Commodities"} />
      <Grid container justifyContent="center">
        <Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Commodities
          </Typography>
        </Grid>
        <Grid item md={12} xs={12} style={{ margin: "10px 18px" }}>
          <TextFieldSearchFilter
            value={filterValue}
            onChange={(val) => {
              setFilterValue(val);
              gridApi?.setGridOption("quickFilterText", val);
            }}
          />
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          style={{ width: "100%", padding: "0 10px" }}
          spacing={2}
        >
          <Grid item md={4} xs={12}>
            <div style={{ height: "75vh", width: "100%" }}>
              <AgGridReact<Commodity>
                rowData={rows || []}
                columnDefs={columnDefs}
                defaultColDef={columnSimpleDefaults}
                rowHeight={34}
                headerHeight={34}
                animateRows
                rowSelection="single"
                getContextMenuItems={getContextMenuItems}
                onGridReady={(params) => setGridApi(params.api)}
                getRowId={(params) => String(params.data.CommodityId)}
                loading={isLoading}
                onRowSelected={onRowSelected}
                gridOptions={{
                  statusBar: {
                    statusPanels: [
                      {
                        statusPanel: "agTotalRowCountComponent",
                        align: "right",
                      },
                      {
                        statusPanel: (params: IStatusPanelParams) => (
                          <AddCommodityStatusPanel
                            handleNewCommodity={handleNewCommodity}
                          />
                        ),
                        align: "left",
                      },
                    ],
                  },
                }}
              />
            </div>
          </Grid>
          <Grid item md={8} xs={12}>
            <div style={{ height: "75vh", width: "100%" }}>
              <CommodityDetails
                commodity={commodity}
                modal={showModal}
                onHide={onModalHide}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>
      <MesfModal
        title={`DELETE COMMODITY`}
        open={showModal === "delete"}
        handleClose={() => setShowModal("")}
        maxWidth="md"
      >
        <MesfModal.Content style={{ padding: "15px 30px" }}>
          {isLoading && <CenteredLazyLoading />}
          {!isLoading && (
            <>
              <Grid container spacing={1}>
                <Grid item md={12} xs={12}>
                  <Typography>
                    Are you sure you want to delete{" "}
                    <strong>{commodity?.CommodityName}</strong> commodity?
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}
        </MesfModal.Content>
        <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => setShowModal("")}
              >
                Cancel
              </Button>
            </Grid>
            <Grid item md={3} xs={12} style={{ margin: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={handleDeleteCommodity}
              >
                DELETE
              </Button>
            </Grid>
          </Grid>
        </MesfModal.Actions>
      </MesfModal>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
